import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { uploadFile, fetchUploads } from "../../store/thunks/uploadsThunk";
import { logInfo, logError } from "../../utils/logger";

export default function FileUpload() {
  const dispatch = useDispatch();
  const client = useSelector((state) => state.clients.selectedClient);
  const userId = useSelector((state) => state.auth.user?._id);
  const [newFolder, setNewFolder] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [uploadResponse, setUploadResponse] = useState(null);
  const [existingFolders, setExistingFolders] = useState([]);

  // Utility function to sanitize folder names (replace spaces with underscores)
  const sanitizeFolderName = (name) => name.trim().replace(/\s+/g, "_");

  // Fetch existing folders when client is selected
  useEffect(() => {
    if (client?._id) {
      dispatch(fetchUploads(client._id))
        .then((resultAction) => {
          if (fetchUploads.fulfilled.match(resultAction)) {
            const files = resultAction.payload;
            if (files && Array.isArray(files)) {
              const folderSet = new Set();
              files.forEach((filePath) => {
                const parts = filePath.split("/");
                if (parts.length > 1) {
                  folderSet.add(parts[0]); // Extract top-level folder
                }
              });
              setExistingFolders(Array.from(folderSet));
              logInfo("Fetched existing folders", {
                clientId: client._id,
                folders: Array.from(folderSet),
              });
            } else {
              logError("Unexpected format for uploaded files", {
                clientId: client._id,
                payload: resultAction.payload,
              });
            }
          } else {
            logError("Failed to fetch uploads", {
              clientId: client._id,
              error: resultAction.error,
            });
          }
        })
        .catch((error) =>
          logError("Error fetching uploads", {
            clientId: client._id,
            error: error.message,
          })
        );
    }
  }, [client, dispatch]);

  // Handle file upload
  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!file || !folderName.trim()) {
      setUploadResponse({
        message: "Please provide both a file and a valid folder name",
        type: "error",
      });
      logError("File upload attempted without file or valid folder name");
      return;
    }

    const sanitizedFolderName = sanitizeFolderName(folderName);

    try {
      const formData = new FormData();
      formData.append("document", file);
      formData.append("folderName", sanitizedFolderName);
      if (password) formData.append("password", password);

      const resultAction = await dispatch(
        uploadFile({
          clientId: client?._id ?? "",
          formData,
          folderName: sanitizedFolderName,
          userId,
        })
      );

      if (uploadFile.fulfilled.match(resultAction)) {
        setUploadResponse({
          message: "File uploaded successfully",
          type: "success",
        });
        setFile(null);
        setFolderName("");
        setPassword("");
        logInfo("File uploaded successfully", {
          clientId: client?._id,
          folderName: sanitizedFolderName,
        });
      } else {
        setUploadResponse({
          message: resultAction.payload || "File upload failed",
          type: "error",
        });
        logError("File upload failed", {
          clientId: client?._id,
          folderName: sanitizedFolderName,
          error: resultAction.payload,
        });
      }
    } catch (error) {
      setUploadResponse({
        message: "An unexpected error occurred",
        type: "error",
      });
      logError("Unexpected error during file upload", {
        clientId: client?._id,
        folderName: sanitizedFolderName,
        error: error.message,
      });
    }
  };

  return (
    <div data-component="FileUpload" className="bg-white rounded-sm shadow-sm border border-gray-200 p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 tracking-tight">Upload Documents</h3>
        <p className="text-sm text-gray-500 mt-1">Upload files for this client with optional password protection.</p>
      </div>

      <form onSubmit={handleFileUpload} className="space-y-4">
        {/* Folder Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Folder Name
          </label>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              className="inline-flex items-center rounded-sm bg-gray-900 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 transition-colors"
              onClick={() => setNewFolder(!newFolder)}>
              {newFolder ? "Select Existing Folder" : "New Folder"}
            </button>
            <select
              className="block w-1/2 rounded-sm border border-gray-300 shadow-sm py-2 pl-3 pr-8 text-sm focus:border-gray-900 focus:ring-gray-900"
              value={newFolder ? "" : folderName}
              onChange={(e) =>
                setFolderName(sanitizeFolderName(e.target.value))
              }
              disabled={newFolder}
              required>
              <option value="" disabled>
                Select an existing folder
              </option>
              {existingFolders.map((folder) => (
                <option key={folder} value={folder}>
                  {folder}
                </option>
              ))}
            </select>
          </div>
          {newFolder && (
            <input
              type="text"
              className="mt-2 block w-full rounded-sm border border-gray-300 shadow-sm text-sm focus:border-gray-900 focus:ring-gray-900 px-3 py-2"
              value={folderName}
              onChange={(e) =>
                setFolderName(sanitizeFolderName(e.target.value))
              }
              placeholder="Enter new folder name"
              required
            />
          )}
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload Document
          </label>
          <div className="rounded-sm border border-red-200 bg-red-50 p-3 mb-2">
            <p className="text-sm text-red-700">
              NOTE: Spaces and special characters are not permitted in file names.
            </p>
          </div>
          <input
            type="file"
            className="block w-full text-sm text-gray-500 border border-gray-300 rounded-sm shadow-sm focus:border-gray-900 focus:ring-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-sm file:font-medium file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100"
            onChange={(e) => {
              const file = e.target.files[0];

              if (file) {
                // Extract the file extension
                const fileNameParts = file.name.split(".");
                const extension = fileNameParts.pop(); // Get the last part as extension
                const baseName = fileNameParts.join("."); // Remaining part as filename

                // Match backend sanitization: Replace non-alphanumeric characters except `_` and `.` with `_`
                const sanitizedBaseName = baseName.replace(/[^\w.-]/g, "_");

                // Reconstruct the sanitized filename
                const sanitizedFileName = `${sanitizedBaseName}.${extension}`;

                // Create a new File object with the sanitized filename
                const newFile = new File([file], sanitizedFileName, {
                  type: file.type,
                });

                setFile(newFile);
                logInfo("File selected for upload", {
                  original: file.name,
                  sanitized: sanitizedFileName,
                });
              } else {
                console.error("No file selected");
                logError("No file selected for upload");
              }
            }}
            required
          />
        </div>

        {/* Password Protection */}
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
              checked={showPassword}
              onChange={() => setShowPassword(!showPassword)}
            />
            <label className="ml-2 text-sm text-gray-700">
              This file is password protected
            </label>
          </div>
          {showPassword && (
            <div className="flex items-center space-x-2">
              <input
                type="password"
                className="block w-full max-w-xs rounded-sm border border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 px-3 py-2 text-sm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
              />
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 p-2"
                onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? (
                  <EyeIcon className="h-5 w-5" />
                ) : (
                  <EyeSlashIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-2">
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-sm shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 transition-colors">
            Upload
          </button>
        </div>
      </form>

      {uploadResponse && (
        <div
          className={`mt-4 p-3 rounded-sm text-sm ${
            uploadResponse.type === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}>
          {uploadResponse.message}
        </div>
      )}
    </div>
  );
}
